import type { IETFLanguageTag } from '@opallabs/concepts'
import { isPositiveNumber } from '../naturalNumbers'
import { validateLocale } from '../validateLocale'

type Segmenters = {
  grapheme: Intl.Segmenter
  word: Intl.Segmenter
}
// Creating a segmenter is fairly slow--according to a quick test with
// `jsbench.me` caching a segmenter is about 10× faster than creating a new one
// each time.
//
// @note This needs to be a `Map` instead of an object because
//   `IETFLanguageTag` can be an array.
const segmenters: Map<IETFLanguageTag, Segmenters> = new Map()

type Options = Readonly<{
  locale?: IETFLanguageTag
  maximumInitials?: number
}>

/**
 * Extract initials from a string. Safely handles multibyte characters like
 * emoji, non-Latin scripts (including languages like Japanese that don’t use
 * whitespace to separate words), and composed Unicode graphemes like ‘é’ (‘`’
 * + ‘e’).
 *
 * @note The default value for `maximumInitials` is somewhat arbitrary. It
 *   should be high enough to return all possible initials in reasonable cases.
 */
export const extractInitials = (
  input: string,
  { locale = navigator.language, maximumInitials = 10 }: Options = {}
): string | typeof input => {
  if (typeof input !== 'string') return input
  if (!validateLocale(locale)) throw new RangeError(`Invalid locale: ${locale}`)
  if (!Number.isInteger(maximumInitials)) {
    throw new TypeError(`Invalid maximumInitials: ${maximumInitials}`)
  }
  if (!isPositiveNumber(maximumInitials)) {
    throw new RangeError(`Invalid maximumInitials: ${maximumInitials}`)
  }

  const sanitized = input.trim()
  if (sanitized === '') return ''

  if (!segmenters.has(locale)) {
    segmenters.set(locale, {
      grapheme: new Intl.Segmenter(locale, {
        granularity: 'grapheme',
      }),
      word: new Intl.Segmenter(locale, {
        granularity: 'word',
      }),
    })
  }

  const wordSegments = Array.from(
    segmenters.get(locale)!.word.segment(sanitized)
  )
  const words = []
  for (const word of wordSegments) {
    if (word.isWordLike) words.push(word.segment)
    if (words.length >= maximumInitials) break
  }
  // If there are no usable words -- note that emoji are not word-like -- short-circuit instead of
  // returning an empty string
  if (words.length === 0) return sanitized

  const graphemes = words.reduce((memo, word) => {
    const iterator = segmenters
      .get(locale)!
      .grapheme.segment(word)
      [Symbol.iterator]()
    const { segment: grapheme } = iterator.next().value ?? {}
    if (grapheme !== undefined) {
      memo += grapheme
    }

    return memo
  }, '')
  return graphemes
}
